/**
 * Thành phần dùng chung của menu "Bảng điện tử công cộng":
 *   - `HopXemTep`  popup xem trước / xem lại tệp bản tin (theo popup "Nghe" của MH-16):
 *     ảnh dùng <img>, video <video controls>, âm thanh <audio controls>;
 *   - `thongBaoKetQuaGui` câu báo kết quả gửi lệnh xuống thiết bị;
 *   - `loiThanhChuoi` lấy câu lỗi từ ngoại lệ.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viBangTin } from '../ngon-ngu/vi-bang-tin';
import type { KetQuaGuiBT, LinkTepBT } from '../api/bang-tin-api';
import './lich-nhat-ky.css';
import './bang-tin.css';

const tc = viBangTin.chung;

export function loiThanhChuoi(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Câu báo kết quả gửi lệnh (phát ngay, khẩn cấp, điều khiển, phát lịch). */
export function thongBaoKetQuaGui(
  kq: KetQuaGuiBT | null | undefined,
  macDinh: string,
): {
  loai: 'ok' | 'loi';
  noiDung: string;
} {
  const loi = kq?.loi ?? [];
  if (loi.length > 0)
    return {
      loai: 'loi',
      noiDung: dienVao(tc.ketQuaGuiCoLoi, {
        phien: kq?.phienId ?? '—',
        so: loi.length,
        chiTiet: loi
          .map((l) => (l.bangDienTuId ? `${l.bangDienTuId}: ${l.thongBao}` : l.thongBao))
          .join('; '),
      }),
    };
  return {
    loai: 'ok',
    noiDung: kq?.phienId ? `${macDinh} ${dienVao(tc.ketQuaGui, { phien: kq.phienId })}` : macDinh,
  };
}

/* ------------------------------ Ngày giờ theo giờ Việt Nam ------------------------------ */

/** Giờ Việt Nam = UTC+7, không có giờ mùa hè. */
const LECH_VN_GIAY = 7 * 3600;
const hai = (n: number): string => String(n).padStart(2, '0');

function phanVn(epochGiay: number): Date {
  return new Date((epochGiay + LECH_VN_GIAY) * 1000);
}

/** Epoch giây → 'YYYY-MM-DD' (giá trị ô ngày) theo giờ Việt Nam. */
export function epochSangONgay(epochGiay: number): string {
  const d = phanVn(epochGiay);
  return `${d.getUTCFullYear()}-${hai(d.getUTCMonth() + 1)}-${hai(d.getUTCDate())}`;
}

/** Epoch giây → 'dd/MM/yyyy' theo giờ Việt Nam. */
export function ngayVn(epochGiay: number | null | undefined): string {
  if (typeof epochGiay !== 'number' || !Number.isFinite(epochGiay)) return '—';
  const d = phanVn(epochGiay);
  return `${hai(d.getUTCDate())}/${hai(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/** Giây trong ngày → 'HH:mm'. */
export function giaySangGio(giay: number): string {
  const s = ((Math.floor(giay) % 86_400) + 86_400) % 86_400;
  return `${hai(Math.floor(s / 3600))}:${hai(Math.floor((s % 3600) / 60))}`;
}

/** 'HH:mm' → giây trong ngày; sai khuôn trả null. */
export function gioSangGiay(gio: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(gio);
  if (!m) return null;
  const h = Number(m[1]);
  const p = Number(m[2]);
  if (h > 23 || p > 59) return null;
  return h * 3600 + p * 60;
}

/** Epoch giây → giây trong ngày theo giờ Việt Nam. */
export function giayTrongNgayVn(epochGiay: number): number {
  return (((epochGiay + LECH_VN_GIAY) % 86_400) + 86_400) % 86_400;
}

export type KieuTep = 'anh' | 'am-thanh' | 'video';

/** Chọn thẻ hiển thị theo loại bản tin; không rõ loại thì theo MIME, rồi theo đuôi đường dẫn. */
export function kieuTep(loaiBanTin: string | null | undefined, url?: string, mime?: string): KieuTep {
  if (loaiBanTin === '2') return 'anh';
  if (loaiBanTin === '3') return 'am-thanh';
  if (loaiBanTin === '4') return 'video';
  if (mime?.startsWith('image/')) return 'anh';
  if (mime?.startsWith('audio/')) return 'am-thanh';
  if (mime?.startsWith('video/')) return 'video';
  const duoi = (url ?? '').split('?')[0]?.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(duoi)) return 'anh';
  if (['mp3', 'wav', 'ogg', 'aac'].includes(duoi)) return 'am-thanh';
  return 'video';
}

/** Phần hiển thị tệp (ảnh / âm thanh / video) — dùng cả trong popup và ở bước tải tệp. */
export function TrinhXemTep({ url, kieu, nhan }: { url: string; kieu: KieuTep; nhan: string }): ReactNode {
  if (kieu === 'anh') return <img className="bt-xem__anh" src={url} alt={nhan} data-kieu-tep="anh" />;
  if (kieu === 'am-thanh')
    return (
      <audio
        className="nl-hop__audio"
        controls
        preload="auto"
        src={url}
        data-kieu-tep="am-thanh"
        aria-label={nhan}
      >
        <track kind="captions" />
      </audio>
    );
  return (
    <video
      className="bt-xem__video"
      controls
      preload="metadata"
      src={url}
      data-kieu-tep="video"
      aria-label={nhan}
    >
      <track kind="captions" />
    </video>
  );
}

export interface HopXemTepProps {
  tieuDe: string;
  loaiBanTin: string | null | undefined;
  /** Lấy link ký ngắn hạn của tệp. */
  layUrl: (signal: AbortSignal) => Promise<LinkTepBT>;
  thongBaoKhongCo: string;
  onDong: () => void;
}

/** Popup xem tệp bản tin: lấy link ký mới mỗi lần mở. */
export function HopXemTep({
  tieuDe,
  loaiBanTin,
  layUrl,
  thongBaoKhongCo,
  onDong,
}: HopXemTepProps): ReactNode {
  const [trang, datTrang] = useState<{ loai: 'tai' } | { loai: 'co'; link: LinkTepBT } | { loai: 'khong' }>({
    loai: 'tai',
  });

  useEffect(() => {
    const boHuy = new AbortController();
    layUrl(boHuy.signal)
      .then((r) => {
        if (!boHuy.signal.aborted) datTrang(r?.url ? { loai: 'co', link: r } : { loai: 'khong' });
      })
      .catch(() => {
        if (!boHuy.signal.aborted) datTrang({ loai: 'khong' });
      });
    return () => boHuy.abort();
    // Chỉ lấy link một lần khi mở popup.
  }, []);

  return (
    <div className="man-che" role="presentation" onClick={onDong}>
      <div
        className="hop-thoai nl-hop bt-xem"
        data-vung="xem-tep"
        role="dialog"
        aria-modal="true"
        aria-label={tieuDe}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="nl-hop__tieu-de">{tieuDe}</h2>
        {trang.loai === 'tai' ? <p>{viBangTin.banTin.dangTaiTep}</p> : null}
        {trang.loai === 'co' ? (
          <TrinhXemTep
            url={trang.link.url}
            kieu={kieuTep(loaiBanTin ?? trang.link.loaiBanTin, trang.link.url, trang.link.tepMime)}
            nhan={tieuDe}
          />
        ) : null}
        {trang.loai === 'khong' ? <p role="alert">{thongBaoKhongCo}</p> : null}
        <div className="nl-hop__nut">
          <button type="button" className="nut" onClick={onDong}>
            {tc.dong}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Dải thông báo kết quả thao tác (cùng lớp `dai-thong-bao` của các trang lịch phát). */
export function DaiThongBao({ tb }: { tb: { loai: 'ok' | 'loi'; noiDung: string } | null }): ReactNode {
  if (!tb) return null;
  return (
    <p
      className={`dai-thong-bao${tb.loai === 'loi' ? ' dai-thong-bao--loi' : ''}`}
      role="status"
      data-vung="thong-bao-thao-tac"
    >
      {tb.noiDung}
    </p>
  );
}
